/**
 * 마지막으로 보던 화면을 기억했다가, 앱이 처음부터 다시 열리면 그 화면으로 돌려보낸다.
 *
 * 홈 화면에 설치한 앱(standalone)은 다른 앱을 쓰는 동안 휴대폰이 메모리를 비우려고
 * 꺼버리는 일이 잦다. 다시 열면 보던 주소가 남지 않고 시작 주소(/)부터 뜬다.
 * 전자책 앱에서 문장을 복사해 붙여넣으러 올 때마다 홈 → 책 → 기록을 다시 눌러야
 * 했던 이유다. 브라우저 탭은 새로고침해도 주소가 남으므로 건드리지 않는다.
 */

export const LAST_SCREEN_KEY = 'br_last_screen';

// 책 한 권을 읽는 동안이면 돌아온다. 그보다 오래 지나 열면 새로 시작하는 것으로 본다
export const RESUME_WINDOW_MS = 3 * 60 * 60 * 1000;

export const readLastScreen = () => {
  try {
    const raw = localStorage.getItem(LAST_SCREEN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const writeLastScreen = (entry) => {
  try { localStorage.setItem(LAST_SCREEN_KEY, JSON.stringify(entry)); }
  catch { /* 저장 실패는 무시 */ }
};

/** 우리 앱 안의 주소인가 ('//다른곳' 처럼 밖으로 나가는 주소는 받지 않는다) */
const isAppPath = (path) =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

/** 기억해 둔 화면이 아직 유효한가 */
export const isFresh = (saved, now) =>
  !!saved && isAppPath(saved.path) && Number.isFinite(saved.t)
  && now - saved.t >= 0 && now - saved.t <= RESUME_WINDOW_MS;

/**
 * 다시 열린 앱을 어느 화면으로 보낼지. 이어서 열 게 아니면 null.
 *
 * @param {object} p
 * @param {{path:string, y:number, t:number}|null} p.saved 기억해 둔 화면
 * @param {string} p.launchPath 앱이 열린 주소 (pathname)
 * @param {boolean} p.standalone 홈 화면에 설치한 앱으로 열렸는가
 * @param {number} p.now
 */
export const pickResumePath = ({ saved, launchPath, standalone, now }) => {
  if (!standalone) return null;          // 브라우저 탭은 주소가 그대로 남는다
  if (launchPath !== '/') return null;   // 링크나 바로가기로 특정 화면을 연 경우
  if (!isFresh(saved, now)) return null;
  return saved.path;
};

export const isStandalone = () => {
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true; // iOS 의 예전 방식
  } catch {
    return false;
  }
};

let resumed = false;

/** 이번 실행이 '보던 화면 이어 열기'였는가 — 스크롤과 열려 있던 창을 되살릴지 정할 때 쓴다 */
export const wasResumed = () => resumed;

/**
 * 앱이 뜨기 전에 부른다. 라우터는 만들어지는 순간의 주소로 시작하므로
 * createBrowserRouter 보다 먼저 주소를 바꿔 둬야 한다 (routes/index.jsx).
 */
export const resumeLastScreen = () => {
  if (typeof window === 'undefined') return;
  const path = pickResumePath({
    saved: readLastScreen(),
    launchPath: window.location.pathname,
    standalone: isStandalone(),
    now: Date.now()
  });
  if (!path) return;

  resumed = true;
  if (path !== window.location.pathname + window.location.search) {
    window.history.replaceState(null, '', path);
  }
};

/** 새로고침이나 앞뒤 이동으로 같은 화면이 다시 그려진 경우 */
export const isReload = () => {
  try {
    const type = performance.getEntriesByType('navigation')[0]?.type;
    return type === 'reload' || type === 'back_forward';
  } catch {
    return false;
  }
};
